import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { StyleSheet, Platform } from 'react-native';

import { beginningOfToday } from '@utils/formatters/date';

import { HoverableDayCell } from './HoverableDayCell';
import theme from '../../../themes/theme';
import { Box, Text } from '../Restyle';
import { WebIcon } from '../shared/Icon/WebIcon';

type DatePickerProps = {
  minDate?: Date;
  maxDate?: Date;
  selectedDate: Date | undefined;
  onSelectDate: (date: Date | undefined) => void;
};

export const MONTH_NAME = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const GetDays = (year: number, month: number) => {
  const days = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)) {
    days[1] = 29;
  }
  return days[month];
};

type DayCellProps = {
  day: number;
  dayStyle?: object;
  textStyle: object;
  isOutsideMonth: boolean;
  disabled: boolean;
  onPress: () => void;
};

// TA-10950 #7/#26: grey01 hover on every selectable date, disabled cursor on
// dates outside min/maxDate.
const DayCell: React.FC<DayCellProps> = ({
  day,
  dayStyle,
  textStyle,
  isOutsideMonth,
  disabled,
  onPress,
}) => {
  const isSelected =
    dayStyle === styles.daySelect || dayStyle === styles.dayToday;

  return (
    <HoverableDayCell
      hoverDisabled={disabled || isSelected}
      touchDisabled={disabled}
      onPress={onPress}
      cursor={disabled ? 'not-allowed' : 'pointer'}
      radius={size / 2}
      width={size}
      height={size}>
      <Box alignItems='center' justifyContent='center'>
        <Text
          variant='metadata'
          allowFontScaling={false}
          style={[
            dayStyle,
            textStyle,
            isOutsideMonth && { color: theme.colors.grey04 },
          ]}>
          {day}
        </Text>
        {isSelected && (
          <Box style={styles.selectedDayContainer}>
            {dayStyle === styles.daySelect && (
              <Box style={styles.selectedDay} />
            )}
            {dayStyle === styles.dayToday && <Box style={styles.dayToday} />}
          </Box>
        )}
      </Box>
    </HoverableDayCell>
  );
};

export const DatePickerWebV3: React.FC<DatePickerProps> = ({
  onSelectDate,
  selectedDate,
  minDate,
  maxDate,
}) => {
  const date = selectedDate ?? beginningOfToday();
  const [year, setYear] = useState(date.getFullYear());
  const [month, setMonth] = useState(date.getMonth());
  const [currentDay, setCurrentDay] = useState(
    selectedDate ? selectedDate.getDate() : undefined
  );
  const [currentMonth, setCurrentMonth] = useState(
    selectedDate ? selectedDate.getMonth() : undefined
  );

  const [currentYear, setCurrentYear] = useState(
    selectedDate?.getFullYear() ?? undefined
  );
  const [weeks, setWeeks] = useState<any[]>([]);
  const [currentMonthDay, setCurrentMonthDay] = useState<any[]>([]);

  useEffect(() => {
    const _weeks = getWeek(currentMonthDay);
    setWeeks(_weeks);
  }, [currentDay, currentMonth, currentYear]);

  useEffect(() => {
    GetMonthData(year, month);
  }, [year, month]);

  const GetMonthData1 = (year: number, month: number) => {
    const monthData: number[] = [];

    // Get number of days in current month
    const currentMonthDays = GetDays(year, month);

    // Get day of week for the first day of current month (0-6, 0 = Sunday)
    const firstDayOfMonth = new Date(year, month, 1);
    const firstDayWeek = firstDayOfMonth.getDay();

    // Get number of days in previous month
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevMonthYear = month === 0 ? year - 1 : year;
    const prevMonthDays = GetDays(prevMonthYear, prevMonth);

    // Add days from previous month (trailing days)
    for (let i = prevMonthDays - firstDayWeek + 1; i <= prevMonthDays; i++) {
      monthData.push(i);
    }

    // Add days from current month
    for (let i = 1; i <= currentMonthDays; i++) {
      monthData.push(i);
    }

    // Add days from next month (leading days) to make total 42 items
    const nextMonthStartDay = 1;
    const totalDaysNeeded = 42;
    const remainingDays = totalDaysNeeded - monthData.length;

    for (
      let i = nextMonthStartDay;
      i < nextMonthStartDay + remainingDays;
      i++
    ) {
      monthData.push(i);
    }

    return monthData;
  };

  const GetMonthData = (year: number, month: number) => {
    const currentMonthDays = GetMonthData1(year, month);

    setCurrentMonthDay(currentMonthDays);
    const _weeks = getWeek(currentMonthDays);
    setWeeks(_weeks);
  };

  const onSelectDay = (day: number, xMonth: number, xYear: number) => {
    const dateSelected = new Date(xYear, xMonth, day);
    if (currentDay == day && currentMonth == xMonth && currentYear == xYear) {
      setCurrentDay(undefined);
      setCurrentYear(undefined);
      setCurrentMonth(undefined);
      onSelectDate(undefined);
      return;
    }
    if (day > 0) {
      if (
        (minDate && minDate > dateSelected) ||
        (maxDate && maxDate < dateSelected)
      ) {
        return;
      } else {
        setCurrentDay(day);
        setCurrentYear(xYear);
        setCurrentMonth(xMonth);
        onSelectDate(new Date(xYear, xMonth, day));
      }
    }
  };

  const monthPre = () => {
    if (month == 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const monthNext = () => {
    if (month == 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  // const doneSelectDate = (): void => {
  //   const date = currentDay
  //     ? new Date(currentYear!, currentMonth!, currentDay)
  //     : undefined;
  //   onSelectDate(date);
  // };

  const setDayStyle = (year: number, month: number, day: number) => {
    const thisDate = new Date(year, month, day);
    const currentDate = new Date(currentYear!, currentMonth!, currentDay!);

    let style = undefined;

    if (
      dayjs(thisDate).format('MM/DD/YYY') ===
      dayjs(new Date()).format('MM/DD/YYY')
    ) {
      style = styles.dayToday;
    }

    if (
      dayjs(thisDate).format('MM/DD/YYY') ===
      dayjs(currentDate).format('MM/DD/YYY')
    ) {
      style = styles.daySelect;
    }

    if (minDate && minDate > thisDate) {
      style = styles.dayNotSelect;
    }

    if (maxDate && maxDate < thisDate) {
      style = styles.dayNotSelect;
    }

    if (day < 1) {
      style = styles.dayViewItem;
    }

    return style;
  };

  const getWeek = (days: number[]) => {
    const _weeks = [];
    let week = [];

    for (let i = 1; i <= days.length; i++) {
      const isLastMonthDay = i < 7 && days[i - 1] > 7;
      const isNextMonthDay = i > 28 && days[i - 1] < 15;
      let xMonth = month;
      let xYear = year;
      if (isLastMonthDay) {
        if (month === 0) {
          xMonth = 11;
          xYear = year - 1;
        } else {
          xMonth = month - 1;
        }
      } else if (isNextMonthDay)
        if (month === 11) {
          xMonth = 0;
          xYear = year + 1;
        } else {
          xMonth = month + 1;
        }

      const dayStyle = setDayStyle(xYear, xMonth, days[i - 1]);
      let textStyle;
      if (dayStyle === styles.dayToday)
        textStyle = { ...styles.dayViewItem, fontFamily: 'Inter_500Medium' };
      else if (dayStyle === styles.daySelect)
        textStyle = { ...styles.dayViewItem, fontSize: 16 };
      else textStyle = { ...styles.dayViewItem };

      week.push(
        <DayCell
          key={year + month + 'touch' + i}
          day={days[i - 1]}
          dayStyle={dayStyle}
          textStyle={textStyle}
          isOutsideMonth={isLastMonthDay || isNextMonthDay}
          disabled={dayStyle === styles.dayNotSelect}
          onPress={() => onSelectDay(days[i - 1], xMonth, xYear)}
        />
      );

      if (i % 7 == 0) {
        _weeks.push(
          <Box
            backgroundColor='white'
            key={year + month + 'week' + i}
            flex={1}
            flexDirection='row'
            paddingTop='none'
            justifyContent='space-between'>
            {week}
          </Box>
        );
        week = [];
      }
    }
    return _weeks;
  };

  let counter = 0;

  return (
    <Box
      pt='xs'
      pb='xs'
      width={360}
      onStartShouldSetResponder={() => true}
      onMoveShouldSetResponder={() => true}
      onResponderStart={(e) => e.stopPropagation()}
      onResponderRelease={(e) => e.stopPropagation()}
      onResponderMove={(e) => e.stopPropagation()}>
      <Box
        height={40}
        pb='xs'
        flexDirection='row'
        borderBottomWidth={1}
        borderBottomColor='grey02'
        alignItems='center'
        justifyContent={Platform.OS === 'web' ? 'space-between' : 'flex-end'}>
        <Text
          flex={1}
          textAlign='center'
          variant='webSmall'
          color='textPrimary'>
          Select Dates
        </Text>
      </Box>
      <Box
        backgroundColor='white'
        mx='m'
        height={44}
        alignItems='center'
        flexDirection='row'
        justifyContent='space-between'>
        <WebIcon
          onPress={monthPre}
          name='ChevronLeft'
          variant='l'
          color='black'
          accessibilityLabel='Previous month'
        />

        <Box flex={1} justifyContent='center'>
          <Text textAlign='center' variant='webSmall' color='textSecondary'>
            {MONTH_NAME[month]} {year}
          </Text>
        </Box>

        <WebIcon
          onPress={monthNext}
          name='ChevronRight'
          variant='l'
          color='black'
          accessibilityLabel='Next month'
        />
      </Box>

      <Box
        height={40}
        backgroundColor='white'
        mx='m'
        alignItems='center'
        flexDirection='row'
        style={[styles.dayContainer]}
        justifyContent='space-between'>
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((i) => {
          counter++;
          return (
            <Text
              key={counter}
              variant='webSmall'
              textAlign='center'
              color='textSecondary'
              style={styles.weekDayText}>
              {i}
            </Text>
          );
        })}
      </Box>
      <Box mx='m'>{weeks}</Box>
    </Box>
  );
};

const size = 32;
const fontSize = 14;
const styles = StyleSheet.create({
  dayViewItem: {
    width: size,
    height: size,
    alignItems: 'center',
    alignContent: 'center',
    textAlign: 'center',
    fontSize: fontSize,
    includeFontPadding: false,
    textAlignVertical: 'center',
    marginVertical: 4,
  },
  daySelect: {
    color: theme.colors.white,
  },
  dayNotSelect: {
    color: theme.colors.grey04,
  },
  dayContainer: {
    // shadowColor: 'black',
    // marginHorizontal: -8,
    // shadowOpacity: 0.06,
    // shadowOffset: { width: 0, height: 12 },
    // shadowRadius: 12,
  },
  selectedDayContainer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: -1,
  },
  dayToday: {
    backgroundColor: theme.colors.white,
    width: size,
    height: size,
    alignSelf: 'center',
    borderRadius: size / 2,
    borderColor: theme.colors.grey05,
    borderWidth: 1,
  },
  selectedDay: {
    backgroundColor: theme.colors.black,
    width: size,
    height: size,
    borderRadius: size / 2,
  },
  weekDayText: {
    width: size,
    textAlign: 'center',
  },
});
